import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useCart } from "../context/CartContext";
import "./ProductDetail.scss";
import {
  IoCartOutline,
  IoCheckmarkCircleOutline,
  IoInformationCircleOutline,
} from "react-icons/io5";

// === Breadcrumb label maps ===
const categoryLabels = {
  merch: "Merch",
  produkty: "Produkty Codaco",
};

const subcategoryLabels = {
  "kratky-rukav": "Krátký rukáv",
  "dlouhy-rukav": "Dlouhý rukáv",
  ostatni: "Ostatní",
  sesterna: "Sesterna",
  pokoj: "Pokoj pacienta",
  socialni: "Sociální zařízení",
  chodba: "Chodba",
  vchod: "Vchod",
  ostatni_produkt: "Ostatní produkty",
};

export default function ProductDetail() {
  const { id } = useParams();

  const [product, setProduct] = useState(null);
  const [mainImage, setMainImage] = useState("");
  const [selectedSize, setSelectedSize] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [direction, setDirection] = useState("next");

  const [touchStartX, setTouchStartX] = useState(null);

  const { addToCart } = useCart();

  const formatPrice = (price) => {
    if (price == null || isNaN(price)) return "";
    return price.toLocaleString("cs-CZ", { useGrouping: true });
  };

  useEffect(() => {
    const fetchProduct = async () => {
      const docRef = doc(db, "products", id);
      const snapshot = await getDoc(docRef);

      if (snapshot.exists()) {
        const data = snapshot.data();
        setProduct(data);

        if (data.images?.length > 0) {
          setMainImage(data.images[0]);
        }
      }
    };
    fetchProduct();
  }, [id]);

  if (!product) return <p>...</p>;

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const prevImage = () => {
    setDirection("prev");
    setLightboxIndex((i) => (i === 0 ? product.images.length - 1 : i - 1));
  };

  const nextImage = () => {
    setDirection("next");
    setLightboxIndex((i) => (i === product.images.length - 1 ? 0 : i + 1));
  };

  const handleTouchStart = (e) => setTouchStartX(e.touches[0].clientX);

  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;

    const diff = touchStartX - e.changedTouches[0].clientX;

    if (diff > 50) nextImage();
    else if (diff < -50) prevImage();

    setTouchStartX(null);
  };

  const catLabel = categoryLabels[product.category] || product.category;
  const subLabel =
    subcategoryLabels[product.subcategory] || product.subcategory;

  const handleSizeSelect = (size) => {
    setSelectedSize(size);
    setAddedToCart(false);
  };

  const handleAddToCart = () => {
    if (!selectedSize && product.sizes?.length > 0) return;

    addToCart(product, selectedSize);
    setAddedToCart(true);
  };

  return (
    <main className="detail">
      <nav className="detail__breadcrumbs">
        <Link to="/">Domů</Link>

        {product.category && (
          <>
            <span>›</span>
            <Link to={`/products/${product.category}`}>{catLabel}</Link>
          </>
        )}

        {product.subcategory && (
          <>
            <span>›</span>
            <Link to={`/products/${product.category}/${product.subcategory}`}>
              {subLabel}
            </Link>
          </>
        )}

        <span>›</span>
        <span className="detail__breadcrumbs-current">{product.name}</span>
      </nav>

      <div className="detail__container">
        {/* --- Gallery --- */}
        <div className="detail__gallery">
          {mainImage && (
            <img
              src={mainImage}
              alt={product.name}
              className="detail__main-image"
              onClick={() => openLightbox(product.images.indexOf(mainImage))}
            />
          )}

          <div className="detail__thumbnails">
            {product.images?.map((img, i) => (
              <img
                key={i}
                src={img}
                alt={`${product.name} ${i + 1}`}
                className={`detail__thumb ${img === mainImage ? "active" : ""}`}
                onClick={() => setMainImage(img)}
              />
            ))}
          </div>
        </div>

        {/* --- Info --- */}
        <div className="detail__info">
          <h1 className="detail__title">{product.name}</h1>

          <p className="detail__price">{formatPrice(product.price)} Kč</p>

          <p className="detail__description">{product.description}</p>

          {product.sizes?.length > 0 && (
            <div className="detail__sizes">
              <span>Dostupné velikosti:</span>
              <ul>
                {product.sizes.map((s, i) => (
                  <li
                    key={i}
                    className={s === selectedSize ? "active" : ""}
                    onClick={() => handleSizeSelect(s)}
                  >
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* === size icon === */}
          <div className="size-chart-icon-wrapper">
            <a
              href="/vel.webp"
              target="_blank"
              rel="noopener noreferrer"
              className="size-chart-icon"
              title="Zobrazit tabulku velikostí"
            >
              <IoInformationCircleOutline />
              <span>Tabulka velikostí</span>
            </a>
          </div>

          <button
            className={`detail__add-btn ${addedToCart ? "added" : ""}`}
            onClick={handleAddToCart}
            disabled={!selectedSize && product.sizes?.length > 0}
          >
            {addedToCart ? (
              <>
                <IoCheckmarkCircleOutline /> Přidáno
              </>
            ) : (
              <>
                <IoCartOutline /> Přidat do košíku
              </>
            )}
          </button>
        </div>
      </div>

      {/* === Lightbox === */}
      {lightboxOpen && (
        <div
          className="lightbox"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <button className="lightbox__close" onClick={closeLightbox}>
            ✕
          </button>

          <button className="lightbox__prev" onClick={prevImage}>
            ‹
          </button>

          <img
            key={lightboxIndex}
            src={product.images[lightboxIndex]}
            alt={`${product.name} ${lightboxIndex + 1}`}
            className={`lightbox__image ${direction}`}
            onClick={closeLightbox}
          />

          <button className="lightbox__next" onClick={nextImage}>
            ›
          </button>
        </div>
      )}
    </main>
  );
}
